import config from '../../config/environment';
import { getDashboardToken, getDashboardTenant, getDashboardUser } from '../../utils/dashboardAuthParams';

/** Express server-master (editor.workload.co:5000) — not Django/Celery. */
const DEFAULT_BASE =
  process.env.REACT_APP_WORKLOAD_PAGES_API_BASE ||
  `${config.EDITOR_URL || config.AGENTS_API_BASE_URL || ''}/api/workload-pages`;

function authHeaders(extra = {}) {
  const headers = { 'Content-Type': 'application/json', ...extra };
  const tok = getDashboardToken();
  const tenant = getDashboardTenant();
  const user = getDashboardUser();
  if (tok) headers.Authorization = `Bearer ${tok}`;
  if (tenant) headers['X-Tenant-Id'] = tenant;
  if (user) headers['X-User-Id'] = user;
  return headers;
}

function encodePageId(pageId) {
  // Keep slash as path segments for readability: tenant/slug
  return String(pageId)
    .split('/')
    .map((s) => encodeURIComponent(s))
    .join('/');
}

async function request(method, path, { query, body, shareToken, skipAuth, tenantId } = {}) {
  const qs = new URLSearchParams();
  if (query) {
    Object.keys(query).forEach((k) => {
      if (query[k] != null && query[k] !== '') qs.set(k, query[k]);
    });
  }
  if (shareToken) qs.set('share', shareToken);
  const url = `${DEFAULT_BASE.replace(/\/$/, '')}/${path.replace(/^\//, '')}${
    qs.toString() ? `?${qs}` : ''
  }`;
  const headers = skipAuth ? { 'Content-Type': 'application/json' } : authHeaders();
  if (tenantId) headers['X-Tenant-Id'] = String(tenantId);
  if (shareToken) headers['X-Share-Token'] = shareToken;

  const res = await fetch(url, {
    method,
    headers,
    body: body != null ? JSON.stringify(body) : undefined,
  });
  let data = null;
  try {
    data = await res.json();
  } catch (_) {
    data = null;
  }
  if (!res.ok) {
    const err = new Error((data && data.error) || `Pages API ${res.status}`);
    err.status = res.status;
    err.details = data && data.details;
    throw err;
  }
  return data;
}

export const pagesClient = {
  baseUrl: DEFAULT_BASE,

  createPage(payload) {
    return request('POST', '', { body: payload });
  },

  updateDraft(pageId, canvas) {
    return request('PUT', `${encodePageId(pageId)}/draft`, { body: { canvas } });
  },

  validate(pageId) {
    return request('POST', `${encodePageId(pageId)}/validate`, { body: {} });
  },

  publish(pageId, body = {}) {
    return request('POST', `${encodePageId(pageId)}/publish`, { body });
  },

  refresh(pageId, snapshots) {
    return request('POST', `${encodePageId(pageId)}/refresh`, { body: { snapshots } });
  },

  getPage(pageId, { preview, version, manifest, shareToken } = {}) {
    return request('GET', encodePageId(pageId), {
      query: {
        preview: preview ? '1' : undefined,
        version,
        manifest,
      },
      shareToken,
      skipAuth: !!shareToken,
    });
  },

  rollback(pageId, canvasVersion) {
    return request('POST', `${encodePageId(pageId)}/rollback`, {
      body: { canvas_version: canvasVersion },
    });
  },

  setVisibility(pageId, policy, { tenantId } = {}) {
    return request('PUT', `${encodePageId(pageId)}/visibility`, {
      body: { policy },
      tenantId,
    });
  },

  createShareToken(pageId, { tenantId } = {}) {
    return request('POST', `${encodePageId(pageId)}/share-token`, {
      body: {},
      tenantId,
    });
  },

  revokeShareToken(pageId, tokenId, { tenantId } = {}) {
    return request('POST', `${encodePageId(pageId)}/share-token/revoke`, {
      body: { token_id: tokenId },
      tenantId,
    });
  },
};

export default pagesClient;
